/**
 * The seven wizard steps. Each is a pure view over the shared `data` object and
 * calls `update(patch)` (shallow merge) to change it. Validation lives in the
 * shell (index.js) and arrives as `errors`; document uploads are driven by the
 * shell too (docStatus / onDocSelect / onDocRemove) so this file stays view-only.
 */

import React from "react"
import {
  BUSINESS_TYPES, businessType, CAB_TYPES, cabTypeLabel,
  INDIAN_STATES, PARTNER_CITIES, RATE_FIELDS, documentsForBusiness, getStateForCity,
} from "../../utils/partnerOnboardingConfig"
import {
  Field, TextInput, NumberInput, SelectInput, RadioCards, CheckChips, FileField,
} from "./Fields"

/* --- 1. Business type ------------------------------------------------ */

export const BusinessTypeStep = ({ data, update }) => {
  const bt = businessType(data.businessType)
  return (
    <div className="pw-step">
      <h2 className="pw-step__title">What kind of partner are you?</h2>
      <p className="pw-step__lead">This decides which documents and tax details we ask for next.</p>
      <RadioCards name="businessType" value={data.businessType} onChange={(n, v) => update({ [n]: v })} options={BUSINESS_TYPES} />
      {data.businessType && (
        <div className="pw-note">
          {bt.gstRequired
            ? "As a company, GST registration and Certificate of Incorporation are required."
            : bt.value === "proprietor"
              ? "GST is optional for a proprietor. Add it if you are registered."
              : "No GST or incorporation needed. Just your driver, vehicle and bank documents."}
        </div>
      )}
    </div>
  )
}

/* --- 2. Business & tax details -------------------------------------- */

export const BusinessTaxStep = ({ data, update, errors }) => {
  const bt = businessType(data.businessType)
  const set = (n, v) => {
    if (n === "city") update({ city: v, state: getStateForCity(v) || data.state })
    else update({ [n]: v })
  }
  const isCompany = bt.value === "company"
  const showGst = bt.gstRequired || bt.value === "proprietor"

  return (
    <div className="pw-step">
      <h2 className="pw-step__title">{isCompany ? "Company & tax details" : "Your details"}</h2>
      <p className="pw-step__lead">Match these exactly to your PAN and registration documents.</p>

      <div className="pw-grid">
        <Field label={isCompany ? "Registered legal name" : "Full name (as per PAN)"} required error={errors.legalName}>
          <TextInput name="legalName" value={data.legalName} onChange={set} placeholder={isCompany ? "Pulpit Mobility Pvt Ltd" : "Your full name"} />
        </Field>
        <Field label="Contact person" error={errors.contactName} hint={isCompany ? "Who we should speak to" : "Leave blank if same as above"}>
          <TextInput name="contactName" value={data.contactName} onChange={set} placeholder="Contact person" />
        </Field>
        <Field label="Email" required error={errors.email}>
          <TextInput name="email" type="email" value={data.email} onChange={set} placeholder="you@example.com" />
        </Field>
        <Field label="Mobile" required error={errors.mobile} hint="Verified number, used for payouts">
          <TextInput name="mobile" value={data.mobile} onChange={set} placeholder="10-digit mobile" maxLength={10} disabled={!!data._mobileLocked} />
        </Field>

        <Field label="PAN" required error={errors.pan} hint="Mandatory for TDS / Form 16A">
          <TextInput name="pan" value={data.pan} onChange={(n, v) => set(n, v.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
        </Field>
        {showGst && (
          <Field label="GSTIN" required={bt.gstRequired} error={errors.gstin} hint={bt.gstRequired ? "15-digit GST number" : "Optional, if registered"}>
            <TextInput name="gstin" value={data.gstin} onChange={(n, v) => set(n, v.toUpperCase())} placeholder="22ABCDE1234F1Z5" maxLength={15} />
          </Field>
        )}
        {isCompany && (
          <>
            <Field label="CIN / registration no." required error={errors.cin}>
              <TextInput name="cin" value={data.cin} onChange={(n, v) => set(n, v.toUpperCase())} placeholder="U60200GJ2024PTC000000" />
            </Field>
            <Field label="Date of incorporation" error={errors.incorporationDate}>
              <TextInput name="incorporationDate" type="date" value={data.incorporationDate} onChange={set} />
            </Field>
          </>
        )}

        <Field label="Registered address" required error={errors.addressLine} full>
          <TextInput name="addressLine" value={data.addressLine} onChange={set} placeholder="Building, street, area" />
        </Field>
        <Field label="City" required error={errors.city}>
          <SelectInput name="city" value={data.city} onChange={set} options={PARTNER_CITIES} placeholder="Select city" />
        </Field>
        <Field label="State" required error={errors.state}>
          <SelectInput name="state" value={data.state} onChange={set} options={INDIAN_STATES} placeholder="Select state" />
        </Field>
        <Field label="PIN code" required error={errors.pincode}>
          <TextInput name="pincode" value={data.pincode} onChange={set} placeholder="380001" maxLength={6} inputMode="numeric" />
        </Field>
      </div>
    </div>
  )
}

/* --- 3. Fleet -------------------------------------------------------- */

export const FleetStep = ({ data, update, errors }) => {
  const offered = data.cabTypesOffered || []
  const vehicles = data.vehicles || []

  const toggleCab = (val) => {
    const next = offered.includes(val) ? offered.filter((v) => v !== val) : [...offered, val]
    update({ cabTypesOffered: next })
  }
  const setVehicle = (i, k, v) => {
    const arr = vehicles.map((row, idx) => (idx === i ? { ...row, [k]: v } : row))
    update({ vehicles: arr })
  }
  const addVehicle = () => update({ vehicles: [...vehicles, { cabType: offered[0] || "", makeModel: "", rcNumber: "", year: "", ac: true }] })
  const removeVehicle = (i) => update({ vehicles: vehicles.filter((_, idx) => idx !== i) })

  return (
    <div className="pw-step">
      <h2 className="pw-step__title">Your fleet</h2>
      <p className="pw-step__lead">Tell us what you run. You can add vehicle details now or after approval.</p>

      <div className="pw-grid">
        <Field label="Cab types you offer" required error={errors.cabTypesOffered} full hint="Pick every type you can supply">
          <CheckChips values={offered} onToggle={toggleCab} options={CAB_TYPES} />
        </Field>
        <Field label="Total number of cabs" required error={errors.numberOfCabs}>
          <NumberInput name="numberOfCabs" value={data.numberOfCabs} onChange={(n, v) => update({ [n]: v })} placeholder="e.g. 5" min="1" />
        </Field>
      </div>

      <div className="pw-subhead">
        <span>Vehicle details <em>(optional now)</em></span>
        <button type="button" className="pw-add" onClick={addVehicle}>+ Add vehicle</button>
      </div>

      {vehicles.length === 0 ? (
        <p className="pw-empty">No vehicles added yet. You can add RC-level details now or later.</p>
      ) : (
        <div className="pw-rows">
          {vehicles.map((v, i) => (
            <div className="pw-row pw-row--vehicle" key={i}>
              <SelectInput name={`v-cab-${i}`} value={v.cabType} onChange={(_, val) => setVehicle(i, "cabType", val)} options={CAB_TYPES} placeholder="Type" />
              <TextInput name={`v-mm-${i}`} value={v.makeModel} onChange={(_, val) => setVehicle(i, "makeModel", val)} placeholder="Make / model" />
              <TextInput name={`v-rc-${i}`} value={v.rcNumber} onChange={(_, val) => setVehicle(i, "rcNumber", val.toUpperCase())} placeholder="RC number" />
              <TextInput name={`v-yr-${i}`} value={v.year} onChange={(_, val) => setVehicle(i, "year", val)} placeholder="Year" maxLength={4} inputMode="numeric" />
              <label className="pw-ac">
                <input type="checkbox" checked={v.ac !== false} onChange={(e) => setVehicle(i, "ac", e.target.checked)} /> AC
              </label>
              <button type="button" className="pw-remove" onClick={() => removeVehicle(i)} aria-label="Remove vehicle">&times;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* --- 4. Rate card (hybrid) ------------------------------------------ */

export const RateCardStep = ({ data, update, errors }) => {
  const offered = data.cabTypesOffered || []
  const rates = data.rates || {}
  const routes = data.topRoutes || []

  const setRate = (cab, k, v) => update({ rates: { ...rates, [cab]: { ...(rates[cab] || {}), [k]: v } } })
  const setRoute = (i, k, v) => update({ topRoutes: routes.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) })
  const addRoute = () => update({ topRoutes: [...routes, { fromCity: "", toCity: "", cabType: offered[0] || "", oneWayFare: "" }] })
  const removeRoute = (i) => update({ topRoutes: routes.filter((_, idx) => idx !== i) })

  if (offered.length === 0) {
    return (
      <div className="pw-step">
        <h2 className="pw-step__title">Rate card</h2>
        <p className="pw-empty">Go back and choose the cab types you offer first, then set rates here.</p>
      </div>
    )
  }

  return (
    <div className="pw-step">
      <h2 className="pw-step__title">Rate card</h2>
      <p className="pw-step__lead">
        Set a per-km rate for each cab type. That alone lets us quote any route.
        Fixed fares for your top city-to-city routes are optional.
      </p>
      {errors.rates && <div className="pw-note pw-note--err">{errors.rates}</div>}

      {offered.map((cab) => (
        <div className="pw-ratecard" key={cab}>
          <div className="pw-ratecard__head">{cabTypeLabel(cab)}</div>
          <div className="pw-ratecard__grid">
            {RATE_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} required={f.required} hint={f.hint}>
                <div className="pw-inputwrap">
                  <span className="pw-inputwrap__unit">{f.unit}</span>
                  <NumberInput
                    name={`${cab}-${f.key}`}
                    value={rates[cab]?.[f.key]}
                    onChange={(_, v) => setRate(cab, f.key, v)}
                    placeholder="0"
                    min={f.min}
                  />
                </div>
              </Field>
            ))}
          </div>
        </div>
      ))}

      <div className="pw-subhead">
        <span>Top city-to-city one-way fares <em>(optional)</em></span>
        <button type="button" className="pw-add" onClick={addRoute}>+ Add route</button>
      </div>

      {routes.length === 0 ? (
        <p className="pw-empty">No fixed routes. Every fare will be computed from your per-km rates above.</p>
      ) : (
        <div className="pw-rows">
          {routes.map((r, i) => (
            <div className="pw-row pw-row--route" key={i}>
              <SelectInput name={`r-from-${i}`} value={r.fromCity} onChange={(_, v) => setRoute(i, "fromCity", v)} options={PARTNER_CITIES} placeholder="From" />
              <SelectInput name={`r-to-${i}`} value={r.toCity} onChange={(_, v) => setRoute(i, "toCity", v)} options={PARTNER_CITIES} placeholder="To" />
              <SelectInput name={`r-cab-${i}`} value={r.cabType} onChange={(_, v) => setRoute(i, "cabType", v)} options={offered.map((c) => ({ value: c, label: cabTypeLabel(c) }))} placeholder="Cab" />
              <div className="pw-inputwrap">
                <span className="pw-inputwrap__unit">₹</span>
                <NumberInput name={`r-fare-${i}`} value={r.oneWayFare} onChange={(_, v) => setRoute(i, "oneWayFare", v)} placeholder="One-way fare" min="0" />
              </div>
              <button type="button" className="pw-remove" onClick={() => removeRoute(i)} aria-label="Remove route">&times;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* --- 5. Documents --------------------------------------------------- */

export const DocumentsStep = ({ data, docStatus, onDocSelect, onDocRemove }) => {
  const groups = documentsForBusiness(data.businessType || "individual")
  const docs = data.documents || {}

  return (
    <div className="pw-step">
      <h2 className="pw-step__title">Documents</h2>
      <p className="pw-step__lead">
        Clear photos or PDFs. Files upload securely straight to Pulpit storage, we never see your bank login.
      </p>

      {Object.entries(groups).map(([group, items]) => (
        <div className="pw-docgroup" key={group}>
          <div className="pw-docgroup__title">{group}</div>
          {items.map((doc) => (
            <FileField
              key={doc.key}
              doc={doc}
              status={docStatus[doc.key] || (docs[doc.key] ? "done" : "idle")}
              meta={docs[doc.key]}
              onSelect={(file) => onDocSelect(doc.key, file)}
              onRemove={() => onDocRemove(doc.key)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/* --- 6. Bank & payout ----------------------------------------------- */

export const BankStep = ({ data, update, errors }) => {
  const bank = data.bank || {}
  const set = (k, v) => update({ bank: { ...bank, [k]: v } })
  return (
    <div className="pw-step">
      <h2 className="pw-step__title">Bank & payout</h2>
      <p className="pw-step__lead">Where your trip earnings are settled. Must match the account holder name on file.</p>
      <div className="pw-grid">
        <Field label="Account holder name" required error={errors.accountName}>
          <TextInput name="accountName" value={bank.accountName} onChange={(_, v) => set("accountName", v)} placeholder="As per bank records" />
        </Field>
        <Field label="Account number" required error={errors.accountNumber}>
          <TextInput name="accountNumber" value={bank.accountNumber} onChange={(_, v) => set("accountNumber", v.replace(/\D/g, ""))} placeholder="Bank account number" inputMode="numeric" />
        </Field>
        <Field label="IFSC" required error={errors.ifsc}>
          <TextInput name="ifsc" value={bank.ifsc} onChange={(_, v) => set("ifsc", v.toUpperCase())} placeholder="HDFC0000123" maxLength={11} />
        </Field>
        <Field label="Bank name" error={errors.bankName}>
          <TextInput name="bankName" value={bank.bankName} onChange={(_, v) => set("bankName", v)} placeholder="Bank & branch" />
        </Field>
        <Field label="UPI ID" hint="Optional, for faster small settlements" full>
          <TextInput name="upi" value={bank.upi} onChange={(_, v) => set("upi", v)} placeholder="name@bank" />
        </Field>
      </div>
    </div>
  )
}

/* --- 7. Review ------------------------------------------------------ */

const Line = ({ k, v }) => (
  <div className="pw-review__line"><span>{k}</span><strong>{v || "-"}</strong></div>
)

export const ReviewStep = ({ data, goTo }) => {
  const bt = businessType(data.businessType)
  const rates = data.rates || {}
  const docs = data.documents || {}
  const uploadedDocs = Object.values(docs).filter(Boolean).length

  return (
    <div className="pw-step">
      <h2 className="pw-step__title">Review & submit</h2>
      <p className="pw-step__lead">Check everything, then submit for verification. You can still edit any section.</p>

      <div className="pw-review">
        <div className="pw-review__block">
          <div className="pw-review__head"><span>Business</span><button type="button" onClick={() => goTo(1)}>Edit</button></div>
          <Line k="Type" v={bt.label} />
          <Line k="Legal name" v={data.legalName} />
          <Line k="Email" v={data.email} />
          <Line k="Mobile" v={data.mobile} />
          <Line k="PAN" v={data.pan} />
          {data.gstin && <Line k="GSTIN" v={data.gstin} />}
          {data.cin && <Line k="CIN" v={data.cin} />}
          <Line k="Location" v={[data.city, data.state].filter(Boolean).join(", ")} />
        </div>

        <div className="pw-review__block">
          <div className="pw-review__head"><span>Fleet</span><button type="button" onClick={() => goTo(2)}>Edit</button></div>
          <Line k="Cab types" v={(data.cabTypesOffered || []).map(cabTypeLabel).join(", ")} />
          <Line k="Total cabs" v={data.numberOfCabs} />
          <Line k="Vehicles detailed" v={String((data.vehicles || []).length)} />
        </div>

        <div className="pw-review__block">
          <div className="pw-review__head"><span>Rates</span><button type="button" onClick={() => goTo(3)}>Edit</button></div>
          {(data.cabTypesOffered || []).map((c) => (
            <Line key={c} k={cabTypeLabel(c)} v={rates[c]?.perKmOneWay ? `₹${rates[c].perKmOneWay}/km one way` : "not set"} />
          ))}
          <Line k="Fixed routes" v={String((data.topRoutes || []).length)} />
        </div>

        <div className="pw-review__block">
          <div className="pw-review__head"><span>Documents</span><button type="button" onClick={() => goTo(4)}>Edit</button></div>
          <Line k="Uploaded" v={`${uploadedDocs} file(s)`} />
        </div>

        <div className="pw-review__block">
          <div className="pw-review__head"><span>Bank</span><button type="button" onClick={() => goTo(5)}>Edit</button></div>
          <Line k="Account holder" v={data.bank?.accountName} />
          <Line k="Account no." v={data.bank?.accountNumber ? `****${String(data.bank.accountNumber).slice(-4)}` : ""} />
          <Line k="IFSC" v={data.bank?.ifsc} />
        </div>
      </div>
    </div>
  )
}
